import React from "react";
import { Link } from "react-router-dom";
import { FiArrowDown, FiArrowRight } from "react-icons/fi";
import "../css/about.scss";

import siteConfig from "../config/siteConfig";

const timeline = [
  {
    year: "01 / Fit",
    title: "De juiste lijn",
    text: "Begin bij de pasvorm: slim, regular, modern of relaxed. Vergelijk de ruimte en de lijn van de pijp.",
  },
  {
    year: "02 / Fabric",
    title: "Het gevoel van stof",
    text: "Van denim tot linnenmix: ontdek hoe structuur en samenstelling het karakter van een broek bepalen.",
  },
  {
    year: "03 / Style",
    title: "Ruimte voor jouw stijl",
    text: "Kies een kleur die je graag draagt en combineer haar met de kleding die al in je kast hangt.",
  },
  {
    year: "04 / Care",
    title: "Aandacht na het dragen",
    text: "Volg het waslabel en bekijk de onderhoudsinformatie bij ieder model.",
  },
];

const craftSteps = [
  {
    number: "01",
    title: "Fit",
    text: "De productinformatie beschrijft de pasvorm, taillehoogte en pijp zodat je modellen kunt vergelijken.",
  },
  {
    number: "02",
    title: "Fabric",
    text: "Samenstelling en structuur geven iedere stof een eigen gevoel. Bekijk de details bij elk model.",
  },
  {
    number: "03",
    title: "Finish",
    text: "Let op details zoals een plooi, een steekzak of een persvouw: kleine verschillen geven karakter.",
  },
];

const values = [
  {
    number: "01",
    title: "Eerlijk",
    text: "Overzichtelijke productinformatie en prijzen helpen je een keuze te maken.",
  },
  {
    number: "02",
    title: "Menselijk",
    text: "Een vraag over de collectie? Je kunt ons rechtstreeks per e-mail bereiken.",
  },
  {
    number: "03",
    title: "Langdurig",
    text: "Kies met aandacht en volg het onderhoudslabel om je kleding goed te verzorgen.",
  },
];

function About() {
  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <main className="about app-page-enter">
      <section className="about-hero" aria-labelledby="about-title">
        <div className="about-hero__copy">
          <p className="eyebrow">The domtanion collection</p>
          <h1 id="about-title">
            Broeken met
            <em>een verhaal.</em>
          </h1>
          <p className="about-hero__intro">
            {siteConfig.description} Ontdek denim, chino’s en geklede modellen en
            maak ruimte voor de pasvorm die bij je past.
          </p>
          <a className="text-link" href="#our-story">
            Ontdek ons verhaal <FiArrowDown aria-hidden="true" />
          </a>
        </div>

        <figure className="about-hero__visual">
          <img
            src="/editorial/about-hero.jpg"
            alt="Light grey fine-check tailored trousers in a quiet studio"
            loading="eager"
          />
          <figcaption>
            <span>01 / Form follows feeling</span>
            <span>Fit — Fabric — Detail</span>
          </figcaption>
        </figure>
      </section>

      <section className="about-manifesto">
        <p className="eyebrow">A good pair of trousers</p>
        <h2>
          Niet alleen hoe een broek <em>staat</em>, maar ook hoe ze gemaakt wordt,
          bepaalt haar waarde.
        </h2>
      </section>

      <section className="about-story" id="our-story">
        <div className="about-story__image">
          <img src="/editorial/about-story.jpg" alt="Ecru straight-leg jeans folded on an oak shelf" loading="lazy" />
          <span>domtanion / Everyday wear</span>
        </div>
        <div className="about-story__copy">
          <p className="eyebrow">Ons verhaal</p>
          <h2>Een collectie met ruimte voor jou.</h2>
          <p>
            Bij {siteConfig.displayName} draait het om broeken die aansluiten bij jouw stijl.
            Onze collectie brengt verschillende pasvormen, kleuren en stoffen samen,
            van ontspannen denim tot een geklede broek.
          </p>
          <p>
            domtanion wordt beheerd door {siteConfig.companyName}. Je vindt ons op
            www.domtanion.art. Voor vragen over de collectie of de website kun je terecht
            bij het e-mailadres op onze contactpagina.
          </p>
          <dl className="about-story__facts">
            <div><dt>24 modellen</dt><dd>Om te ontdekken</dd></div>
            <div><dt>Jouw stijl</dt><dd>Jouw dagelijkse ritme</dd></div>
            <div><dt>1 focus</dt><dd>De perfecte broek</dd></div>
          </dl>
        </div>
      </section>

      <section className="about-timeline" aria-labelledby="timeline-title">
        <header className="about-timeline__heading">
          <p className="eyebrow">Your choice</p>
          <h2 id="timeline-title">Vier stappen naar jouw favoriet.</h2>
        </header>
        <ol>
          {timeline.map((moment, index) => (
            <li key={moment.year}>
              <span className="about-timeline__index">0{index + 1}</span>
              <time>{moment.year}</time>
              <h3>{moment.title}</h3>
              <p>{moment.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="about-origin" aria-labelledby="origin-title">
        <div className="about-origin__copy">
          <p className="eyebrow">One collection / Your wardrobe</p>
          <h2 id="origin-title">
            A considered fit,
            <em>an everyday choice.</em>
          </h2>
          <p>
            Een goede keuze begint bij heldere informatie. Bekijk de pasvorm, kleur en
            samenstelling van elk model. Voor de productieherkomst en het onderhoud
            blijft het label in het kledingstuk de aangewezen bron.
          </p>
          <div className="about-origin__places">
            <div>
              <span>01 / Fit</span>
              <h3>Shape &amp; proportion</h3>
              <p>Van een slanke lijn tot een ruimere, ontspannen pasvorm.</p>
            </div>
            <div>
              <span>02 / Detail</span>
              <h3>Fabric &amp; finish</h3>
              <p>Kijk naar samenstelling, structuur, zakken en plooien.</p>
            </div>
          </div>
        </div>
        <figure>
          <img src="/editorial/about-origin.jpg" alt="Sage linen-cotton trouser waistband and weave" loading="lazy" />
          <figcaption>Texture in every detail.</figcaption>
        </figure>
      </section>

      <section className="about-craft" aria-labelledby="craft-title">
        <div className="about-craft__heading">
          <p className="eyebrow">Inside the trousers</p>
          <h2 id="craft-title">Details die je voelt voordat je ze ziet.</h2>
        </div>
        <div className="about-craft__layout">
          <img src="/editorial/about-craft.jpg" alt="Sand chino pocket and tailored waistband detail" loading="lazy" />
          <div className="about-craft__steps">
            {craftSteps.map((step) => (
              <article key={step.number}>
                <span>{step.number}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="about-people" aria-labelledby="people-title">
        <div className="about-people__copy">
          <p className="eyebrow">Wear it your way</p>
          <h2 id="people-title">Een broek, verschillende momenten.</h2>
          <p>
            Een andere top of schoen geeft dezelfde broek een nieuw gevoel. Ontdek hoe een
            ontspannen combinatie ruimte maakt voor beweging, of hoe een enkele plooi
            een alledaagse outfit een gekleder karakter geeft.
          </p>
        </div>
        <img src="/editorial/about-people.jpg" alt="Tobacco pleated trousers styled for an everyday moment" loading="lazy" />
        <blockquote>
          “Een eigen stijl begint bij wat voor jou goed voelt.”
        </blockquote>
      </section>

      <section className="about-values" aria-labelledby="values-title">
        <header>
          <p className="eyebrow">What matters</p>
          <h2 id="values-title">Onze waarden,<br />zonder kleine lettertjes.</h2>
        </header>
        <div className="about-values__grid">
          {values.map((value) => (
            <article key={value.number}>
              <span>{value.number}</span>
              <h3>{value.title}</h3>
              <p>{value.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="about-cta">
        <p className="eyebrow">Wear the story</p>
        <h2>Vind de broek die met je meegroeit.</h2>
        <div>
          <Link className="solid-button" to="/shop" onClick={scrollTop}>
            Bekijk de collectie <FiArrowRight aria-hidden="true" />
          </Link>
          <Link className="text-link" to="/contact" onClick={scrollTop}>
            Praat met ons <FiArrowRight aria-hidden="true" />
          </Link>
        </div>
      </section>
    </main>
  );
}

export default About;
